import React from 'react';
import RecipeCard from './RecipeCard';

const MEAL_META = {
  breakfast: { label: 'Kahvalti', icon: 'wb_sunny' },
  lunch: { label: 'Ogle', icon: 'restaurant_menu' },
  dinner: { label: 'Aksam', icon: 'dark_mode' }
};

function formatDateLabel(date, dayName) {
  if (!date) return dayName || '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return dayName || date;
  return `${dayName || ''} ${parsed.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}`.trim();
}

// [B14-C3] Gün stok-karşılama rozeti: yeşil (≥80) / amber (50-79) / kırmızı (<50).
function coverageBadgeClass(pct) {
  if (pct >= 80) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
  if (pct >= 50) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
  return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300';
}

// [B14-D2] Slot sebep rozeti — DÜRÜST gerekçe. Tekrar (havuz sınırlı) / stok için küçültüldü.
function slotReasonMeta(recipe) {
  if (!recipe || !recipe.slotReason) return null;
  if (recipe.slotReason === 'repeat_pool_limited') {
    const gap = Number(recipe.repeatGapDays);
    return {
      icon: 'restart_alt',
      text: Number.isFinite(gap) && gap > 0 ? `${gap} gün sonra tekrar · çeşit sınırlı` : 'Havuz sınırlı · çeşit az',
      cls: 'bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300'
    };
  }
  if (recipe.slotReason === 'stock_low') {
    return {
      icon: 'inventory_2',
      text: 'Stok için porsiyon küçültüldü',
      cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
    };
  }
  return null;
}

export default function PlanDaysPreview({
  days = [],
  isDarkMode = false,
  onRecipeClick,
  emptyMessage = 'Bu gorunum icin henuz uygun plan olusturulamadi.',
  showTrainingBadges = false,
  // [B14-D4] Slot değiştirme: (date, mealType, recipe) → swap modalını açar. Verilmezse buton gizli.
  onSwap = null,
  swapTheme = 'green'
}) {
  const swapBtnCls = swapTheme === 'orange'
    ? 'text-orange-600 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/20'
    : 'text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20';
  if (!days.length) {
    return (
      <div className="rounded-[2rem] border border-dashed border-slate-200 bg-white p-8 text-center dark:border-slate-700 dark:bg-slate-800">
        <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <section className="space-y-4">
      {days.map((day) => (
        <article
          key={day.date}
          className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
        >
          <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Plan Gunu</p>
              <h3 className="mt-1 text-lg font-black text-slate-900 dark:text-white">
                {formatDateLabel(day.date, day.dayName)}
              </h3>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* [B14-C3] Stok karşılama rozeti (yalnız haftalık/shop modunda stockCoverage gelir). */}
              {Number.isFinite(Number(day.stockCoverage)) && (
                <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-black ${coverageBadgeClass(Number(day.stockCoverage))}`}>
                  <span className="material-symbols-outlined text-sm">inventory_2</span>
                  Stok %{Number(day.stockCoverage)}
                </div>
              )}
              {showTrainingBadges && (
                <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-black ${
                  day.isTrainingDay
                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                    : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                }`}>
                  <span className="material-symbols-outlined text-sm">
                    {day.isTrainingDay ? 'fitness_center' : 'self_improvement'}
                  </span>
                  {day.isTrainingDay ? 'Antrenman Gunu' : 'Dinlenme Gunu'}
                </div>
              )}
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {['breakfast', 'lunch', 'dinner'].map((mealType) => {
              const meta = MEAL_META[mealType];
              const mealRecipes = day.meals?.[mealType] || [];
              const mealTarget = day.mealTargets?.[mealType];

              // [B12] Per-öğün kalori hedefini tutturmak için porsiyonu ÖLÇEKLE (büyüt/küçült).
              // Tarif 1 porsiyon hedefi tutmuyorsa porsiyon büyür (Sahanda 285→556 ≈ 2),
              // aşıyorsa küçülür. Kart userPortion ile kalori/maliyet/malzemeyi ölçekler.
              const targetCal = Number(mealTarget?.calories) || 0;
              const computeMealPortion = (recipe) => {
                // [B13] Composer porsiyonu (mealPortion) otoritatif — varsa onu kullan.
                const enginePortion = Number(recipe?.mealPortion);
                if (Number.isFinite(enginePortion) && enginePortion > 0) return enginePortion;
                const perServing = Number(recipe?.calories) || 0;
                if (perServing <= 0 || targetCal <= 0) return 1;
                const raw = Math.min(3, Math.max(0.5, targetCal / perServing));
                return Math.round(raw * 2) / 2; // 0.5'lik adımlar
              };

              return (
                <div
                  key={`${day.date}-${mealType}`}
                  className="rounded-[1.5rem] border border-slate-100 bg-slate-50/80 p-4 dark:border-slate-700 dark:bg-slate-900/40"
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-amber-500">{meta.icon}</span>
                      <div>
                        <p className="text-sm font-black text-slate-900 dark:text-white">{meta.label}</p>
                        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">
                          Hedef: {mealTarget?.calories || 0} kcal
                        </p>
                      </div>
                    </div>
                  </div>

                  {mealRecipes.length > 0 ? (
                    <div className="space-y-3">
                      {mealRecipes.map((recipe) => {
                        const reason = slotReasonMeta(recipe);
                        return (
                          <div key={`${day.date}-${mealType}-${recipe.id}`} className="space-y-2">
                            <RecipeCard
                              recipe={recipe}
                              isDarkMode={isDarkMode}
                              onCardClick={() => onRecipeClick?.(recipe)}
                              userPortion={computeMealPortion(recipe)}
                              isCompact={true}
                            />
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              {reason ? (
                                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold ${reason.cls}`}>
                                  <span className="material-symbols-outlined text-[13px]">{reason.icon}</span>
                                  {reason.text}
                                </span>
                              ) : <span />}
                              {onSwap && (
                                <button
                                  type="button"
                                  onClick={() => onSwap(day.date, mealType, recipe)}
                                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-black transition-colors ${swapBtnCls}`}
                                >
                                  <span className="material-symbols-outlined text-[14px]">swap_horiz</span>
                                  Değiştir
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-6 text-center dark:border-slate-700">
                      <p className="text-xs font-semibold text-slate-400">Bu ogun icin onerilen tarif yok.</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </article>
      ))}
    </section>
  );
}
